import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Heart } from "lucide-react";
import { Container, Reveal, Overline } from "../components/common";
import { PageHero } from "../components/PageHero";
import { DonateSection } from "../components/DonateSection";
import { ICONS } from "../components/icons";
import { PROGRAMS, IMAGES } from "../lib/content";
import { fetchPrograms } from "../lib/api";
import { mapProgramRows } from "../lib/dynamicMappers";
import { useApiData } from "../hooks/useApiData";

export default function Programs() {
  const programs = useApiData(fetchPrograms, PROGRAMS, mapProgramRows);

  return (
    <>
      <PageHero
        overline="Programs & Projects"
        title="Six pillars of community transformation"
        subtitle="Each program is a promise — designed to nurture minds, heal bodies and restore dignity to those who need it most."
        image={IMAGES.skillSewing}
      />

      <section className="py-20 lg:py-28 bg-white" data-testid="programs-list">
        <Container>
          <div className="space-y-6 lg:space-y-8">
            {programs.map((p, i) => {
              const Icon = ICONS[p.icon];
              const reverse = i % 2 === 1;
              return (
                <Reveal key={p.id} delay={0.04}>
                  <div
                    id={p.id}
                    data-testid={`program-${p.id}`}
                    className="group grid lg:grid-cols-12 gap-0 rounded-3xl overflow-hidden bg-offwhite border border-slate-100 shadow-soft hover:shadow-card transition-all scroll-mt-24"
                  >
                    <div className={`lg:col-span-5 relative min-h-[240px] overflow-hidden ${reverse ? "lg:order-2" : ""}`}>
                      <img src={p.image} alt={p.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent lg:bg-gradient-to-r" />
                    </div>
                    <div className={`lg:col-span-7 p-8 lg:p-12 flex flex-col justify-center ${reverse ? "lg:order-1" : ""}`}>
                      <div className="flex items-center gap-4">
                        <span className="grid place-items-center h-14 w-14 rounded-2xl bg-teal-700 text-white shadow-soft">
                          <Icon className="h-7 w-7" />
                        </span>
                        <span className="font-mono text-xs uppercase tracking-[0.18em] text-coral">{p.tag}</span>
                      </div>
                      <h3 className="mt-6 font-display text-2xl sm:text-3xl font-extrabold text-navy">{p.name}</h3>
                      <p className="mt-4 text-slate-600 text-lg leading-relaxed max-w-xl">{p.desc}</p>
                      <Link
                        to="/get-involved#donate"
                        data-testid={`support-${p.id}`}
                        className="mt-7 inline-flex items-center gap-2 self-start rounded-full border border-teal-200 px-5 py-3 text-sm font-semibold text-teal-700 hover:bg-teal-700 hover:text-white hover:border-teal-700 transition-colors"
                      >
                        Support this program <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      <DonateSection />
    </>
  );
}
